import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import getCSRFToken from '@utils/csrf'
import TableWebAPI from '@api/TableWebAPI'
import jsonApiNormalize from '@base/json-api-normalizer'
import { receiveEntities } from '@actions/entity_actions'
import { toastSuccess } from '@actions/toast_actions'
import { serializableError } from '@utils/serializable_error'

const loadTableById = async (tableId: string) => {
  const include = ['photo', 'fields', 'fields.select_options', 'rows', 'rows.photos']
  // Propagate any errors to the caller to be handled in queryFn
  const response = await TableWebAPI.loadTable(tableId, include.join(','))
  return { data: response.data as any }
}

export const viewsApi = createApi({
  reducerPath: 'viewsApi',
  tagTypes: ['View'],
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v2/',
    prepareHeaders: headers => {
      const csrfToken = getCSRFToken() ?? ''
      headers.set('X-CSRF-Token', csrfToken)
      return headers
    },
  }),
  endpoints: builder => ({
    getViewByUuid: builder.query<{ view: any; table: any }, { viewUuid: string; tableId: string }>({
      providesTags: (_result, _err, { viewUuid }) => [{ type: 'View', uuid: viewUuid }],
      queryFn: async ({ viewUuid, tableId }, _queryApi, _extraOptions, fetchWithBaseQuery) => {
        try {
          // fetch the view using redux query
          const viewRequest = await fetchWithBaseQuery(`views/${viewUuid}`)
          if ('error' in viewRequest && viewRequest.error) {
            return { error: viewRequest.error }
          }

          // fetch the table using TableWebApi
          const tableRequest = await loadTableById(tableId)

          // return the combined response
          return {
            data: {
              view: (viewRequest as any).data,
              table: tableRequest.data,
            },
          }
        } catch (err) {
          // Normalize error to FetchBaseQueryError shape
          const data = err instanceof Error ? err.message : err
          return { error: { status: 'CUSTOM_ERROR', data } as any }
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          // onSuccess of both queries, perform some side effects
          const { data } = await queryFulfilled
          const normalizedTable = jsonApiNormalize(data.table, { camelizeKeys: false, camelizeTypeValues: false })
          const normalizedView = jsonApiNormalize(data.view, { camelizeKeys: false, camelizeTypeValues: false })
          const normalized = { ...normalizedTable, ...normalizedView }
          // dispatch the receiveEntities action to update normalized state
          dispatch(receiveEntities(normalized))
        } catch (err) {
          console.error(err)
        }
      },
    }),
    updateViewAccessLevel: builder.mutation({
      query: ({ name, externalAccessLevel, viewUuid }) => ({
        url: `views/${viewUuid}`,
        method: 'PATCH',
        body: {
          name,
          external_access_level: externalAccessLevel,
        },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          // onSuccess of views query, perform some side effects
          const { data } = await queryFulfilled
          const normalized = jsonApiNormalize(data, { camelizeKeys: false, camelizeTypeValues: false })
          dispatch(receiveEntities(normalized))
          dispatch(toastSuccess('Share settings updated'))
        } catch (_) {
          return
        }
      },
      transformErrorResponse: err =>
        serializableError({
          response: {
            ...err,
            data: JSON.stringify(err.data),
          },
        }),
    }),
  }),
})

export const { useGetViewByUuidQuery, useUpdateViewAccessLevelMutation } = viewsApi
